// Séjour au Prieuré saint Léonard — retreats, group days, weekends.
import { useEffect, useState } from "react";
import { useI18n } from "@/i18n/I18nContext";
import Ornament from "@/components/Ornament";
import api from "@/lib/api";

const SEO_TITLES = {
  fr: "Logis Prieural — Retraite, journée d'études, week-end",
  en: "Stay at the Priory — Retreat, study day, weekend",
  it: "Soggiornare al Priorato — Ritiro, giornata di studi, week-end",
  es: "Alojarse en el Priorato — Retiro, jornada de estudios, fin de semana",
  de: "Aufenthalt im Priorat — Exerzitien, Studientag, Wochenende",
  pt: "Estadia no Priorado — Retiro, jornada de estudos, fim de semana",
  zh: "在修道院住宿 — 闭关、学习日、周末",
  pl: "Pobyt w Klasztorze — Rekolekcje, dzień studyjny, weekend",
};
const SEO_DESC = {
  fr: "Le Prieuré saint Léonard ouvre ses portes pour vos retraites individuelles, journées d'études et week-ends de ressourcement. Demande de séjour en ligne.",
  en: "The Priory of Saint Léonard welcomes individual retreats, study days and renewal weekends. Online stay request.",
  it: "Il Priorato di San Leonardo accoglie ritiri individuali, giornate di studio e week-end di rinnovamento.",
  es: "El Priorato de San Leonardo acoge retiros individuales, jornadas de estudio y fines de semana de renovación.",
  de: "Das Priorat Saint Léonard empfängt Einzelexerzitien, Studientage und Erneuerungswochenenden.",
  pt: "O Priorado de São Leonardo acolhe retiros individuais, jornadas de estudo e fins de semana de renovação.",
  zh: "圣莱奥纳尔修道院接待个人闭关、学习日和重振周末。",
  pl: "Klasztor św. Leonarda przyjmuje indywidualne rekolekcje, dni studyjne i weekendy odnowy.",
};
import SeoHead from "@/components/SeoHead";

const T = {
  fr: {
    kicker: "Hospitalité au Prieuré",
    title: "Logis Prieural",
    subtitle: "Retraite, journée d'études, week-end de ressourcement",
    introQuote: "Là où l'expérience sensorielle du Profane rejoint le Sacré.",
    introBody: "Le Prieuré saint Léonard ouvre ses portes à celles et ceux qui cherchent un temps de silence, d'étude ou de fraternité. L'accueil s'adresse aux retraitants individuels, aux groupes paroissiaux, aux associations et aux familles qui souhaitent vivre un séjour en cohérence avec l'esprit du lieu.",
    formsTitle: "Plusieurs formules possibles",
    forms: [
      { h: "Retraite individuelle", body: "Quelques jours de silence et de prière, en chambre simple. Repas en commun." },
      { h: "Journée d'études", body: "Une journée sur place pour un groupe, avec salle de travail et déjeuner sur place." },
      { h: "Week-end", body: "Un week-end de ressourcement, formation ou rencontre fraternelle (vendredi soir → dimanche après-midi)." },
      { h: "Séjour de groupe", body: "Accueil de groupes constitués (paroisses, scouts, équipes pro), avec programme sur mesure." },
    ],
    conditionsTitle: "Conditions d'accueil",
    conditions: [
      "Être adhérent aux Amis du Prieuré saint Léonard (ou que le groupe le soit).",
      "Respect du règlement intérieur du Prieuré.",
    ],
    tariffsTitle: "Tarifs",
    rooms: [
      { label: "1 chambre single", price: "100 €" },
      { label: "2 chambres doubles", price: "150 €" },
    ],
    taxDeductible: "L'association est d'intérêt général : adhésions et dons ouvrent droit à un reçu fiscal (réduction d'impôt de 66 % dans la limite de 20 % du revenu imposable, art. 200 du CGI).",
    formTitle: "Faire une demande de réservation de séjour",
    formIntro: "Décrivez-nous votre projet — nous reviendrons vers vous avec une proposition.",
    fields: {
      firstName: "Prénom", lastName: "Nom", email: "E-mail", phone: "Téléphone (optionnel)",
      group: "Nom du groupe / paroisse / association (optionnel)",
      sejourType: "Type de séjour",
      nbPeople: "Nombre de personnes",
      arrivalDate: "Date d'arrivée souhaitée",
      departureDate: "Date de départ souhaitée",
      project: "Message",
      member: "J'atteste être adhérent aux Amis du Prieuré saint Léonard (ou que mon groupe est adhérent).",
    },
    typeOptions: {
      retraite_individuelle: "Retraite individuelle",
      journee: "Journée",
      week_end: "Week-end",
      groupe: "Séjour de groupe",
      autre: "Autre",
    },
    cta: "Envoyer la demande",
    sending: "Envoi en cours…",
    successTitle: "Demande reçue, merci !",
    successBody: "Notre équipe reviendra vers vous très prochainement. Un e-mail de confirmation vient de vous être envoyé.",
  },
  en: {
    kicker: "Hospitality at the Priory",
    title: "Priory Lodging",
    subtitle: "Retreat, study day, restorative weekend",
    introQuote: "Where the sensory experience of the Profane meets the Sacred.",
    introBody: "The Priory of Saint Léonard opens its doors to those who seek a time of silence, study or fellowship. We welcome individual retreatants, parish groups, associations and families wishing to live a stay in harmony with the spirit of the place.",
    formsTitle: "Several possible formulas",
    forms: [
      { h: "Individual retreat", body: "A few days of silence and prayer in a single room. Shared meals." },
      { h: "Study day", body: "A day on site for a group, with workspace and lunch on the premises." },
      { h: "Weekend", body: "A weekend of renewal, training or fraternal gathering (Friday evening → Sunday afternoon)." },
      { h: "Group stay", body: "Welcome of established groups (parishes, scouts, professional teams), with bespoke programme." },
    ],
    conditionsTitle: "Welcome conditions",
    conditions: [
      "Be a member of the Friends of the Priory of Saint Léonard (or that the group is a member).",
      "Indicative capacity: up to 40 persons depending on the formula and season.",
      "Free contribution (donativo), paid on site. French tax receipt on request (Art. 200 CGI).",
      "Respect of the Priory's rules and spiritual vocation.",
    ],
    taxDeductible: "The association is recognised as being of general interest: memberships and donations entitle French tax residents to a tax receipt (66% income tax reduction within 20% of taxable income, Art. 200 of the French Tax Code).",
    formTitle: "Submit a stay request",
    formIntro: "Tell us about your project — we will get back to you with a proposal.",
    fields: {
      firstName: "First name", lastName: "Last name", email: "E-mail", phone: "Phone (optional)",
      group: "Group / parish / association name (optional)",
      sejourType: "Type of stay",
      nbPeople: "Number of persons",
      arrivalDate: "Desired arrival date",
      departureDate: "Desired departure date",
      project: "Your project: intentions, topic, expectations…",
      member: "I certify being a member of the Friends of the Priory of Saint Léonard (or that my group is a member).",
    },
    typeOptions: {
      retraite_individuelle: "Individual retreat",
      journee: "Day",
      week_end: "Weekend",
      groupe: "Group stay",
      autre: "Other",
    },
    cta: "Send request",
    sending: "Sending…",
    successTitle: "Request received, thank you!",
    successBody: "Our team will get back to you very soon. A confirmation e-mail has just been sent to you.",
  },
};
// Other locales spread from FR (form fully translatable later by native speaker)
T.it = { ...T.fr, title: "Alloggio Priorale", subtitle: "Ritiro, giornata di studio, weekend di rigenerazione", kicker: "Ospitalità al Priorato" };
T.es = { ...T.fr, title: "Alojamiento Prioral", subtitle: "Retiro, jornada de estudio, fin de semana de descanso", kicker: "Hospitalidad en el Priorato" };
T.de = { ...T.fr, title: "Prioratswohnung", subtitle: "Einkehr, Studientag, Wochenende der Besinnung", kicker: "Gastfreundschaft im Priorat" };
T.pt = { ...T.fr, title: "Alojamento Prioral", subtitle: "Retiro, dia de estudo, fim de semana de renovação", kicker: "Hospitalidade no Priorado" };
// Russian — full content translation
T.ru = {
  kicker: "Гостеприимство в приорстве",
  title: "Приоратское жилище",
  subtitle: "Затвор, день обучения, выходные для восстановления сил",
  introQuote: "Там, где чувственный опыт мирского встречается со Священным.",
  introBody: "Приорство Сен-Леонар открывает двери тем, кто ищет время тишины, изучения или братского общения. Приём предназначен для индивидуальных затворников, приходских групп, ассоциаций и семей, желающих провести пребывание в согласии с духом этого места.",
  formsTitle: "Несколько возможных формул",
  forms: [
    { h: "Индивидуальный затвор", body: "Несколько дней тишины и молитвы в одноместной комнате. Совместные трапезы." },
    { h: "День обучения", body: "День на месте для группы — с рабочим залом и обедом на месте." },
    { h: "Выходные", body: "Выходные для восстановления, обучения или братской встречи (с вечера пятницы по воскресенье после полудня)." },
    { h: "Групповое пребывание", body: "Приём сложившихся групп (приходы, скауты, профессиональные команды), с программой по запросу." },
  ],
  conditionsTitle: "Условия приёма",
  conditions: [
    "Быть членом Друзей приорства Сен-Леонар (или чтобы группа была членом).",
    "Ориентировочная вместимость: до 40 человек в зависимости от формулы и сезона.",
    "Свободный вклад (donativo), оплачивается на месте. Налоговая квитанция по запросу (ст. 200 NК).",
    "Уважение к внутреннему распорядку приорства и его духовному призванию.",
  ],
  taxDeductible: "Ассоциация имеет статус общественной пользы: членские взносы и пожертвования дают французским налоговым резидентам право на налоговую квитанцию (снижение налога на 66 % в пределах 20 % налогооблагаемого дохода, ст. 200 Налогового кодекса).",
  formTitle: "Подать заявку на пребывание",
  formIntro: "Опишите ваш проект — мы вернёмся к вам с предложением.",
  fields: {
    firstName: "Имя", lastName: "Фамилия", email: "Эл. почта", phone: "Телефон (необязательно)",
    group: "Название группы / прихода / ассоциации (необязательно)",
    sejourType: "Тип пребывания",
    nbPeople: "Количество человек",
    arrivalDate: "Желаемая дата прибытия",
    departureDate: "Желаемая дата отъезда",
    project: "Ваш проект: намерения, тематика, ожидания…",
    member: "Подтверждаю, что являюсь членом Друзей приорства Сен-Леонар (или что моя группа состоит в ассоциации).",
  },
  typeOptions: {
    retraite_individuelle: "Индивидуальный затвор",
    journee: "День",
    week_end: "Выходные",
    groupe: "Групповое пребывание",
    autre: "Другое",
  },
  cta: "Отправить заявку",
  sending: "Отправка…",
  successTitle: "Заявка получена, благодарим!",
  successBody: "Наша команда свяжется с вами в ближайшее время. Письмо с подтверждением только что отправлено.",
};
// Chinese (Simplified) — form-critical override
T.zh = {
  ...T.fr,
  kicker: "修道院的接待",
  title: "修道院寓所",
  subtitle: "闭关、学习日、重振周末",
  formTitle: "提交住宿申请",
  formIntro: "请向我们介绍您的计划——我们将与您联系并提出方案。",
  conditionsTitle: "接待条件",
  conditions: [
    "须为圣莱奥纳尔修道院之友(或本团体为会员)。",
    "参考容量:根据形式和季节,最多可接待40人。",
    "自由捐献(donativo),现场支付。可应要求开具法国税务收据(税法第200条)。",
    "尊重修道院的内部规章及精神使命。",
  ],
  fields: {
    firstName: "名", lastName: "姓", email: "电子邮件", phone: "电话(可选)",
    group: "团体 / 教区 / 协会名称(可选)",
    sejourType: "住宿类型",
    nbPeople: "人数",
    arrivalDate: "希望到达日期",
    departureDate: "希望离开日期",
    project: "您的计划:意向、主题、期望……",
    member: "我证明本人是圣莱奥纳尔修道院之友的会员(或本团体是会员)。",
  },
  typeOptions: {
    retraite_individuelle: "个人闭关",
    journee: "学习日",
    week_end: "周末",
    groupe: "团体住宿",
    autre: "其他",
  },
  cta: "提交申请", sending: "提交中…",
  successTitle: "已收到您的申请,谢谢!",
  successBody: "我们的团队将很快与您联系。确认邮件已发送给您。",
};
// Polish — form-critical override
T.pl = {
  ...T.fr,
  kicker: "Gościnność w Klasztorze",
  title: "Mieszkanie Klasztorne",
  subtitle: "Rekolekcje, dzień studyjny, weekend odnowy",
  formTitle: "Złóż prośbę o pobyt",
  formIntro: "Opowiedz nam o swoim projekcie — wrócimy do Ciebie z propozycją.",
  conditionsTitle: "Warunki przyjęcia",
  conditions: [
    "Być członkiem Przyjaciół Klasztoru św. Leonarda (lub aby grupa była członkiem).",
    "Pojemność orientacyjna: do 40 osób w zależności od formuły i sezonu.",
    "Wolny datek (donativo), płatny na miejscu. Francuski paragon podatkowy na życzenie (art. 200 NК).",
    "Poszanowanie regulaminu Klasztoru i jego duchowego powołania.",
  ],
  fields: {
    firstName: "Imię", lastName: "Nazwisko", email: "E-mail", phone: "Telefon (opcjonalnie)",
    group: "Nazwa grupy / parafii / stowarzyszenia (opcjonalnie)",
    sejourType: "Rodzaj pobytu",
    nbPeople: "Liczba osób",
    arrivalDate: "Pożądana data przyjazdu",
    departureDate: "Pożądana data wyjazdu",
    project: "Twój projekt: intencje, temat, oczekiwania…",
    member: "Oświadczam, że jestem członkiem Przyjaciół Klasztoru św. Leonarda (lub że moja grupa jest członkiem).",
  },
  typeOptions: {
    retraite_individuelle: "Rekolekcje indywidualne",
    journee: "Dzień",
    week_end: "Weekend",
    groupe: "Pobyt grupowy",
    autre: "Inne",
  },
  cta: "Wyślij prośbę", sending: "Wysyłanie…",
  successTitle: "Prośba otrzymana, dziękujemy!",
  successBody: "Nasz zespół wkrótce się z Tobą skontaktuje. Właśnie wysłaliśmy do Ciebie e-mail potwierdzający.",
};
// Ukrainian + Romanian — form-critical overrides
T.uk = {
  ...T.fr,
  kicker: "Гостинність у пріорстві",
  title: "Пріорське житло",
  subtitle: "Усамітнення, день навчання, відновлювальні вихідні",
  formTitle: "Подати заявку на перебування",
  formIntro: "Опишіть ваш проєкт — ми повернемося до вас із пропозицією.",
  conditionsTitle: "Умови прийому",
  conditions: [
    "Бути членом Друзів пріорства Сен-Леонар (або щоб група була членом).",
    "Орієнтовна місткість: до 40 осіб залежно від формули та сезону.",
    "Вільний внесок (donativo), сплачується на місці. Французька податкова квитанція за запитом (ст. 200 ПК).",
    "Повага до внутрішнього розпорядку пріорства та його духовного покликання.",
  ],
  fields: {
    firstName: "Ім'я", lastName: "Прізвище", email: "Ел. пошта", phone: "Телефон (необов'язково)",
    group: "Назва групи / парафії / асоціації (необов'язково)",
    sejourType: "Тип перебування",
    nbPeople: "Кількість осіб",
    arrivalDate: "Бажана дата прибуття",
    departureDate: "Бажана дата від'їзду",
    project: "Ваш проєкт: наміри, тематика, очікування…",
    member: "Підтверджую, що я є членом Друзів пріорства Сен-Леонар (або що моя група є членом).",
  },
  typeOptions: {
    retraite_individuelle: "Індивідуальне усамітнення",
    journee: "День",
    week_end: "Вихідні",
    groupe: "Групове перебування",
    autre: "Інше",
  },
  cta: "Надіслати заявку", sending: "Надсилання…",
  successTitle: "Заявку отримано, дякуємо!",
  successBody: "Наша команда зв'яжеться з вами найближчим часом. Лист із підтвердженням щойно надіслано.",
};
T.ro = {
  ...T.fr,
  kicker: "Ospitalitate la mănăstire",
  title: "Locuință Mănăstirească",
  subtitle: "Reculegere, zi de studiu, weekend de regăsire",
  formTitle: "Trimite o cerere de ședere",
  formIntro: "Povestește-ne despre proiectul tău — vom reveni cu o propunere.",
  conditionsTitle: "Condiții de primire",
  conditions: [
    "Să fiți membru al Prietenilor Mănăstirii sfântului Leonard (sau ca grupul să fie membru).",
    "Capacitate orientativă: până la 40 de persoane în funcție de formulă și sezon.",
    "Contribuție liberă (donativo), plătibilă la fața locului. Chitanță fiscală franceză la cerere (art. 200 CFI).",
    "Respectarea regulamentului interior al mănăstirii și a vocației sale spirituale.",
  ],
  fields: {
    firstName: "Prenume", lastName: "Nume", email: "E-mail", phone: "Telefon (opțional)",
    group: "Numele grupului / parohiei / asociației (opțional)",
    sejourType: "Tip de ședere",
    nbPeople: "Număr de persoane",
    arrivalDate: "Data dorită de sosire",
    departureDate: "Data dorită de plecare",
    project: "Proiectul tău: intenții, tematică, așteptări…",
    member: "Atest că sunt membru al Prietenilor Mănăstirii sfântului Leonard (sau că grupul meu este membru).",
  },
  typeOptions: {
    retraite_individuelle: "Reculegere individuală",
    journee: "Zi",
    week_end: "Weekend",
    groupe: "Ședere de grup",
    autre: "Altul",
  },
  cta: "Trimite cererea", sending: "Se trimite…",
  successTitle: "Cerere primită, mulțumim!",
  successBody: "Echipa noastră vă va contacta în curând. Tocmai am trimis un e-mail de confirmare.",
};

const EMPTY = {
  first_name: "", last_name: "", email: "", phone: "",
  group_name: "", sejour_type: "retraite_individuelle",
  nb_people: 1, arrival_date: "", departure_date: "",
  project: "", member_attestation: false,
};

export default function Sejour() {
  const { locale } = useI18n();
  const tr = T[locale] || T.fr;
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { window.scrollTo({ top: 0 }); }, []);

  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((p) => ({ ...p, [k]: v }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await api.post("/sejour/request", {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        group_name: form.group_name.trim(),
        sejour_type: form.sejour_type,
        nb_people: Number(form.nb_people),
        arrival_date: form.arrival_date,
        departure_date: form.departure_date,
        project: form.project,
        member_attestation: true,
        locale,
      });
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="sejour-page">
      <SeoHead titles={SEO_TITLES} descriptions={SEO_DESC} image="/photos/prieure-accueil.jpg"
        keywords="hébergement baie du Mont-Saint-Michel, hôtel Mont-Saint-Michel, hébergement de charme Normandie, gîte de charme, chambre d'hôtes, Airbnb premium, Booking, séjour spirituel, retraite spirituelle, hospitalité monastique, séjour Normandie, Vains, Avranches" />
      {/* HERO */}
      <section className="relative" style={{ height: "55vh", minHeight: 380 }}>
        <img src="/photos/prieure-accueil.jpg" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, rgba(20,16,12,0.55), rgba(20,16,12,0.40))" }} />
        <div className="container-prieure relative h-full flex flex-col justify-end pb-12">
          <div className="font-caps text-[0.7rem] mb-3" style={{ color: "var(--gold)", letterSpacing: "0.28em" }}>{tr.kicker}</div>
          <h1 className="font-display text-4xl sm:text-6xl text-white mb-3">{tr.title}</h1>
        </div>
      </section>

      {/* Quote + intro — retirés le 2026-02-19 à la demande de l'admin */}

      {/* "Plusieurs formules" — retiré le 2026-02-19 à la demande de l'admin */}

      {/* Form */}
      <section className="py-20" style={{ background: "var(--parchment)" }} data-testid="sejour-form-section">
        <div className="container-prieure max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <Ornament className="mb-6" />
            <h2 className="font-display text-3xl sm:text-4xl">{tr.formTitle}</h2>
          </div>

          <aside className="p-6 mb-10" style={{ background: "white", borderLeft: "3px solid var(--gold)" }}>
            <div className="font-caps text-[0.6rem] mb-3" style={{ color: "var(--gold)", letterSpacing: "0.22em" }}>
              {tr.tariffsTitle || "TARIFS"}
            </div>
            <ul className="space-y-2">
              {tr.rooms.map((r, i) => (
                <li key={i} className="flex items-baseline justify-between gap-3 font-body text-[0.95rem]" style={{ color: "var(--ink-2)" }}>
                  <span className="flex items-start gap-3">
                    <span className="font-display" style={{ color: "var(--cinnabar)" }}>•</span>
                    <span>{r.label}</span>
                  </span>
                  <span className="font-display font-medium" style={{ color: "var(--ink)" }}>{r.price}</span>
                </li>
              ))}
            </ul>
          </aside>

          {done ? (
            <div data-testid="sejour-form-success" className="p-10 text-center bg-white" >
              <div style={{ color: "var(--gold)", fontSize: "2.5rem" }}>◇</div>
              <h3 className="font-display text-2xl mt-3">{tr.successTitle}</h3>
              <p className="font-body mt-3" style={{ color: "var(--ink-2)" }}>{tr.successBody}</p>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="sejour-form" className="grid grid-cols-1 sm:grid-cols-2 gap-5 bg-white p-7 border" style={{ borderColor: "var(--border)" }}>
              <F label={tr.fields.firstName} required>
                <input data-testid="sejour-first-name" required value={form.first_name} onChange={set("first_name")} className="form-input" />
              </F>
              <F label={tr.fields.lastName} required>
                <input data-testid="sejour-last-name" required value={form.last_name} onChange={set("last_name")} className="form-input" />
              </F>
              <F label={tr.fields.email} required>
                <input data-testid="sejour-email" type="email" required value={form.email} onChange={set("email")} className="form-input" />
              </F>
              <F label={tr.fields.phone}>
                <input data-testid="sejour-phone" value={form.phone} onChange={set("phone")} className="form-input" />
              </F>
              <F label={tr.fields.nbPeople} required>
                <input data-testid="sejour-nb-people" type="number" min="1" max="40" required value={form.nb_people} onChange={set("nb_people")} className="form-input" />
              </F>
              <F label={tr.fields.arrivalDate}>
                <input data-testid="sejour-arrival" type="date" value={form.arrival_date} onChange={set("arrival_date")} className="form-input" />
              </F>
              <F label={tr.fields.departureDate}>
                <input data-testid="sejour-departure" type="date" value={form.departure_date} onChange={set("departure_date")} className="form-input" />
              </F>
              <F label={tr.fields.project} className="sm:col-span-2">
                <textarea data-testid="sejour-project" rows="4" value={form.project} onChange={set("project")} className="form-input" />
              </F>

              {error && <div className="sm:col-span-2 text-sm" style={{ color: "var(--cinnabar)" }} data-testid="sejour-form-error">{error}</div>}

              <div className="sm:col-span-2 flex justify-center mt-3">
                <button type="submit" data-testid="sejour-submit" disabled={busy}
                        className="btn btn-primary"
                        style={{ fontSize: "0.78rem", padding: "0.95rem 2.4rem", letterSpacing: "0.18em" }}>
                  {busy ? tr.sending : tr.cta}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}

function F({ label, children, required, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="font-caps text-[0.6rem] block mb-1.5" style={{ color: "var(--ink-2)", letterSpacing: "0.2em" }}>
        {label} {required && <span style={{ color: "var(--cinnabar)" }}>*</span>}
      </span>
      {children}
    </label>
  );
}
